import { useEffect, useRef, useState } from "react";
import {
  analyticsIsAvailable,
  getAnalyticsConsent,
  setAnalyticsConsent,
  type AnalyticsConsentChoice,
  type AnalyticsPreferencesOpenDetail,
} from "./analytics";
import { useLocale } from "./locale";

const consentCopy = {
  en: {
    title: "Reading statistics and privacy",
    summary: "Atlas uses Google Analytics to understand which texts are read. Limited cookieless signals are sent before your choice and if you continue without cookies. Allowing cookies helps connect pages read and recognize return visits; advertising and personalization remain disabled.",
    detailsLabel: "What data is sent",
    detailsCloseLabel: "Hide data details",
    text: "Google Analytics loads with cookies disabled. Before you choose—and if you decline—it sends limited cookieless signals: the page path and, when present, only the campaign parameters utm_source, utm_medium, utm_campaign, utm_content, and utm_term; search text, all other query parameters, and URL fragments are removed. It also sends time, referrer, basic device or browser data, approximate location derived from the connection, and consent state. Google may use these for aggregate or modeled statistics. If you allow, cookies and a pseudonymous browser identifier also link pages read and recognize return visits. Advertising and personalization remain disabled. You can change this choice at any time in the footer.",
    more: "How Google uses this data",
    moreHref: "https://policies.google.com/technologies/partner-sites?hl=en",
    allow: "Allow cookies for statistics",
    decline: "Continue without cookies",
  },
  uk: {
    title: "Статистика читання та приватність",
    summary: "Атлас використовує Google Analytics, щоб розуміти, які тексти читають. Обмежені сигнали без cookie надсилаються до вашого вибору й якщо ви продовжите без cookie. Дозвіл cookie допомагає пов’язувати прочитані сторінки та розпізнавати повторні відвідування; реклама й персоналізація залишаються вимкненими.",
    detailsLabel: "Які дані передаються",
    detailsCloseLabel: "Сховати опис даних",
    text: "Google Analytics завантажується з вимкненими cookie. До вашого вибору й у разі відмови він надсилає обмежені сигнали без cookie: шлях сторінки та, за їх наявності, лише параметри кампанії utm_source, utm_medium, utm_campaign, utm_content і utm_term; пошуковий текст, усі інші параметри запиту та фрагменти URL видаляються. Він також надсилає час, джерело переходу, основні дані пристрою або браузера, приблизне місцеперебування за мережевим з’єднанням і стан згоди. Google може використовувати їх для зведеної або змодельованої статистики. Якщо ви дозволите, cookie та псевдонімний ідентифікатор також зв’язуватимуть прочитані сторінки та розпізнаватимуть повторні відвідування. Реклама й персоналізація залишаються вимкненими; змінити вибір можна внизу сторінки.",
    more: "Як Google використовує ці дані",
    moreHref: "https://policies.google.com/technologies/partner-sites?hl=uk",
    allow: "Дозволити cookie для статистики",
    decline: "Продовжити без cookie",
  },
  ru: {
    title: "Статистика чтения и приватность",
    summary: "Атлас использует Google Analytics, чтобы понимать, какие тексты читают. Ограниченные сигналы без cookies отправляются до вашего выбора и если вы продолжите без cookies. Разрешение cookies помогает связывать прочитанные страницы и распознавать повторные посещения; реклама и персонализация остаются отключёнными.",
    detailsLabel: "Какие данные передаются",
    detailsCloseLabel: "Скрыть описание данных",
    text: "Google Analytics загружается с отключёнными cookies. До вашего выбора и при отказе он отправляет ограниченные сигналы без cookies: путь страницы и, при их наличии, только параметры кампании utm_source, utm_medium, utm_campaign, utm_content и utm_term; поисковый текст, все остальные параметры запроса и фрагменты URL удаляются. Он также отправляет время, источник перехода, основные данные устройства или браузера, примерное местоположение по сетевому соединению и состояние согласия. Google может использовать их для сводной или моделируемой статистики. Если вы разрешите, cookies и псевдонимный идентификатор также свяжут прочитанные страницы и распознают повторные посещения. Реклама и персонализация остаются отключёнными; изменить выбор можно внизу страницы.",
    more: "Как Google использует эти данные",
    moreHref: "https://policies.google.com/technologies/partner-sites?hl=ru",
    allow: "Разрешить cookies для статистики",
    decline: "Продолжить без cookies",
  },
};

export function AnalyticsConsent() {
  const { language } = useLocale();
  const [choice, setChoice] = useState<AnalyticsConsentChoice | null>(getAnalyticsConsent);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [focusRequest, setFocusRequest] = useState(0);
  const headingRef = useRef<HTMLElement>(null);
  const firstActionRef = useRef<HTMLButtonElement>(null);
  const invokerRef = useRef<HTMLElement | null>(null);
  const returnFocusRef = useRef(false);
  const copy = consentCopy[language];

  useEffect(() => {
    const open = (event: Event) => {
      const invoker = (event as CustomEvent<AnalyticsPreferencesOpenDetail>).detail?.invoker;
      invokerRef.current = invoker instanceof HTMLElement ? invoker : null;
      setDetailsOpen(false);
      setChoice(null);
      if (invokerRef.current) setFocusRequest((request) => request + 1);
    };
    const changed = (event: Event) => setChoice((event as CustomEvent<AnalyticsConsentChoice>).detail);
    window.addEventListener("atlas:analytics-preferences-open", open);
    window.addEventListener("atlas:analytics-consent-change", changed);
    return () => {
      window.removeEventListener("atlas:analytics-preferences-open", open);
      window.removeEventListener("atlas:analytics-consent-change", changed);
    };
  }, []);

  useEffect(() => {
    if (focusRequest === 0 || choice !== null) return;
    (headingRef.current ?? firstActionRef.current)?.focus();
  }, [choice, focusRequest]);

  useEffect(() => {
    if (choice === null || !returnFocusRef.current) return;
    returnFocusRef.current = false;
    const invoker = invokerRef.current;
    invokerRef.current = null;
    if (invoker?.isConnected) invoker.focus();
  }, [choice]);

  if (!analyticsIsAvailable() || choice !== null) return null;

  const choose = (next: AnalyticsConsentChoice) => {
    returnFocusRef.current = true;
    setAnalyticsConsent(next);
    setChoice(next);
  };

  return (
    <aside className="analytics-consent" aria-labelledby="analytics-consent-title">
      <div className="analytics-consent-copy">
        <strong
          id="analytics-consent-title"
          ref={headingRef}
          role="heading"
          aria-level={2}
          tabIndex={-1}
        >
          {copy.title}
        </strong>
        <p className="analytics-consent-summary">{copy.summary}</p>
        <button
          type="button"
          className="analytics-consent-details-toggle"
          aria-expanded={detailsOpen}
          aria-controls="analytics-consent-details"
          onClick={() => setDetailsOpen((open) => !open)}
        >
          {detailsOpen ? copy.detailsCloseLabel : copy.detailsLabel}
        </button>
        {detailsOpen && (
          <div className="analytics-consent-details" id="analytics-consent-details">
            <p>{copy.text}</p>
            <a href={copy.moreHref} target="_blank" rel="noreferrer">{copy.more}</a>
          </div>
        )}
      </div>
      <div className="analytics-consent-actions">
        <button ref={firstActionRef} type="button" className="analytics-allow" onClick={() => choose("granted")}>{copy.allow}</button>
        <button type="button" onClick={() => choose("denied")}>{copy.decline}</button>
      </div>
    </aside>
  );
}
